import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { authAxios, QueryStatus } from "../../../../utils";
import { RootState } from "../../../../redux/store";
import { getAdvancedAnalyticsVehiclesGroupByVehicle } from "./tripLib";
import {
  DriverPlanningByDay,
  GetTrips,
  Trips,
  TripsByVehicleDriverDayApi,
} from "./tripsApi";

export interface FleetState {
  tripsByDayStatus: QueryStatus;
  tripsByVehicleDriverDay: Trips[];
  planningsByDayStatus: QueryStatus;
  planningsByDay: DriverPlanningByDay[];
}

const initialState: FleetState = {
  tripsByDayStatus: "idle",
  tripsByVehicleDriverDay: [],
  planningsByDayStatus: "idle",
  planningsByDay: [],
};

export const tripsAsync = createAsyncThunk(
  "trips/call",
  async ({ vehicles }: GetTrips, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<TripsByVehicleDriverDayApi[]>(
      `trips/company/${company?.uuid}/vehicles`,
    );

    const advancedAnalyticsVehicleGroupByVehicle =
      getAdvancedAnalyticsVehiclesGroupByVehicle(vehicles);

    return response.data
      .map((res) => {
        const isVehicle =
          advancedAnalyticsVehicleGroupByVehicle[res.vehicleUuid];
        if (!isVehicle) {
          return null;
        }
        return {
          vehicleUuid: res.vehicleUuid,
          date: res.date,
          usageStartDate: res.usageStartDate,
          usageEndDate: res.usageEndDate,
          totalDistanceInKm: res.totalDistanceInKm,
          count: res.count,
          driver: res.driver,
          trips: res.trips,
          hoursDuration: res.hoursDuration,
          registrationNumber:
            advancedAnalyticsVehicleGroupByVehicle[res.vehicleUuid]
              .registrationNumber,
          reference:
            advancedAnalyticsVehicleGroupByVehicle[res.vehicleUuid].reference,
          vehicleModel: [
            advancedAnalyticsVehicleGroupByVehicle[res.vehicleUuid].marque,
            advancedAnalyticsVehicleGroupByVehicle[res.vehicleUuid].modele,
          ]
            .filter((d) => d)
            .join(" "),
          vehicleType:
            advancedAnalyticsVehicleGroupByVehicle[res.vehicleUuid].vehicleType,
          vehicleAnalyticCode:
            advancedAnalyticsVehicleGroupByVehicle[res.vehicleUuid]
              .vehicleAnalyticCode,
        };
      })
      .filter((d) => d !== null);
  },
);

export const tripsByDepartmentAsync = createAsyncThunk(
  "tripsByDepartmentAsync/call",
  async ({ vehicles }: GetTrips, thunkAPI) => {
    const departmentUuid = (thunkAPI.getState() as RootState)
      .departmentsForDepartmentUser.currentDepartmentUuid;
    if (!departmentUuid) {
      throw new Error("Department not selected");
    }
    const axios = authAxios({ departmentUuid });
    const response = await axios.get<TripsByVehicleDriverDayApi[]>(
      `departments/trips/department/${departmentUuid}`,
    );
    const advancedAnalyticsVehicleGroupByVehicle =
      getAdvancedAnalyticsVehiclesGroupByVehicle(vehicles);

    return response.data
      .map((res) => {
        const isVehicle =
          advancedAnalyticsVehicleGroupByVehicle[res.vehicleUuid];
        if (!isVehicle) {
          return null;
        }
        return {
          ...res,
          registrationNumber:
            advancedAnalyticsVehicleGroupByVehicle[res.vehicleUuid]
              .registrationNumber,
          reference:
            advancedAnalyticsVehicleGroupByVehicle[res.vehicleUuid].reference,
          vehicleModel: [
            advancedAnalyticsVehicleGroupByVehicle[res.vehicleUuid].marque,
            advancedAnalyticsVehicleGroupByVehicle[res.vehicleUuid].modele,
          ]
            .filter((d) => d)
            .join(" "),
          vehicleType:
            advancedAnalyticsVehicleGroupByVehicle[res.vehicleUuid].vehicleType,
          vehicleAnalyticCode:
            advancedAnalyticsVehicleGroupByVehicle[res.vehicleUuid]
              .vehicleAnalyticCode,
        };
      })
      .filter((d) => d !== null);
  },
);

export const planningsAsync = createAsyncThunk(
  "planningsAsync/call",
  async (payload: { companyUuid: string }, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<DriverPlanningByDay[]>(
      `driver_plannings/company/${payload.companyUuid}`,
    );
    return response.data;
  },
);

export const planningsByDepartmentAsync = createAsyncThunk(
  "planningsByDepartmentAsync/call",
  async (_, thunkAPI) => {
    const departmentUuid = (thunkAPI.getState() as RootState)
      .departmentsForDepartmentUser.currentDepartmentUuid;
    if (!departmentUuid) {
      throw new Error("Department not selected");
    }
    const axios = authAxios({ departmentUuid });
    const response = await axios.get<DriverPlanningByDay[]>(
      `departments/driver_plannings/department/${departmentUuid}`,
    );
    return response.data;
  },
);

export const tripSlice = createSlice({
  name: "trip",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    resetTripsListStatus(state) {
      state.tripsByDayStatus = "idle";
    },
    resetPlanningsListStatus(state) {
      state.planningsByDayStatus = "idle";
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(tripsAsync.pending, (state) => {
        state.tripsByDayStatus = "processing";
      })
      .addCase(tripsAsync.fulfilled, (state, action) => {
        state.tripsByVehicleDriverDay = action.payload;
        state.tripsByDayStatus = "success";
      })
      .addCase(tripsAsync.rejected, (state) => {
        state.tripsByDayStatus = "failed";
      })
      .addCase(tripsByDepartmentAsync.pending, (state) => {
        state.tripsByDayStatus = "processing";
      })
      .addCase(tripsByDepartmentAsync.fulfilled, (state, action) => {
        state.tripsByVehicleDriverDay = action.payload;
        state.tripsByDayStatus = "success";
      })
      .addCase(tripsByDepartmentAsync.rejected, (state) => {
        state.tripsByDayStatus = "failed";
      })
      .addCase(planningsAsync.pending, (state) => {
        state.planningsByDayStatus = "processing";
      })
      .addCase(planningsAsync.fulfilled, (state, action) => {
        state.planningsByDay = action.payload;
        state.planningsByDayStatus = "success";
      })
      .addCase(planningsAsync.rejected, (state) => {
        state.planningsByDayStatus = "failed";
      })
      .addCase(planningsByDepartmentAsync.pending, (state) => {
        state.planningsByDayStatus = "processing";
      })
      .addCase(planningsByDepartmentAsync.fulfilled, (state, action) => {
        state.planningsByDay = action.payload;
        state.planningsByDayStatus = "success";
      })
      .addCase(planningsByDepartmentAsync.rejected, (state) => {
        state.planningsByDayStatus = "failed";
      });
  },
});

export const selectTripsByDayList = (state: RootState) =>
  state.trips.tripsByVehicleDriverDay;
export const selectTripsByDayStatus = (state: RootState) =>
  state.trips.tripsByDayStatus;
export const selectPlanningsByDayList = (state: RootState) =>
  state.trips.planningsByDay;
export const selectPlanningsByDayStatus = (state: RootState) =>
  state.trips.planningsByDayStatus;

export default tripSlice.reducer;
